/**
 * Formats a collector may upload and no browser will draw.
 *
 * `ACCEPTED_DOCUMENT_TYPES` lets TIFF and HEIC through — a scan bureau returns
 * TIFF and an iPhone returns HEIC, and refusing either would refuse the two
 * commonest ways paperwork actually arrives. Chrome and Firefox decode neither,
 * so a viewer that only offered them as downloads asked a verifier to leave the
 * screen and open a file manager to look at the thing they were asked about.
 *
 * They are decoded HERE instead, in the reader's own browser, and only when
 * somebody opens one — see `decodeToImage` for why the decoders are imported
 * dynamically. Safari draws HEIC natively, and decoding it anyway costs one
 * conversion of a file that is already on the wire; a viewer that branched on
 * the browser would be two code paths where one works everywhere.
 */
export const DECODABLE_TYPES = ["image/tiff", "image/heic", "image/heif"];

/** HEIC and HEIF are the same container; the extension is the only difference. */
const HEIF_TYPES = ["image/heic", "image/heif"];

/**
 * The bytes of one document, turned into something an `<img>` can show.
 *
 * Returns an object URL the CALLER must revoke — `useDecodedImage` does, on the
 * effect's cleanup. Throws rather than returning null, so the slide can say
 * which of the two things failed: fetching the bytes or decoding them.
 *
 * Both decoders are `import()`ed rather than imported at the top of the file.
 * The HEIC one carries a WebAssembly build of libheif and is measured in
 * megabytes, and a collector who never opens a HEIC must not pay for it on
 * first paint — the same argument this screen already makes for signing a URL
 * only when somebody asks to look.
 */
export async function decodeToImage(url: string, contentType: string): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`The bytes could not be fetched (${response.status}).`);
  }

  const blob = await response.blob();
  const decoded = HEIF_TYPES.includes(contentType)
    ? await decodeHeif(blob)
    : await decodeTiff(blob);

  return URL.createObjectURL(decoded);
}

async function decodeHeif(blob: Blob): Promise<Blob> {
  const { heicTo } = await import("heic-to");

  // PNG rather than JPEG: a document photographed for evidence is read, not
  // admired, and a lossy re-encode of a page of text is the one artefact a
  // reader could mistake for a defect in the original.
  return heicTo({ blob, type: "image/png" });
}

/**
 * TIFF, via the same decoder Photopea uses.
 *
 * A TIFF is a container: it may hold several pages, and this takes the first,
 * which is what a scanned document's first page is. Multi-page evidence is a
 * real thing and this does not pretend to solve it — the download link under
 * the slide still hands over the whole file.
 */
async function decodeTiff(blob: Blob): Promise<Blob> {
  const loaded = await import("utif2");
  // utif2 is CommonJS — one `module.exports = UTIF`. A bundler hands that to an
  // ESM caller under `default`, and only sometimes also as named exports, so
  // taking either is the difference between working and a runtime undefined.
  const UTIF = loaded.default ?? loaded;

  const buffer = await blob.arrayBuffer();

  const pages = UTIF.decode(buffer);
  const [first] = pages;
  if (!first) throw new Error("This TIFF holds no image.");

  UTIF.decodeImage(buffer, first);
  const rgba = UTIF.toRGBA8(first);

  const width = first.width;
  const height = first.height;
  if (!width || !height) throw new Error("This TIFF does not say how big it is.");

  return toPng(rgba, width, height);
}

/** Raw RGBA onto a canvas, and off it as PNG bytes. */
async function toPng(rgba: Uint8Array, width: number, height: number): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) throw new Error("This browser would not give up a canvas.");

  // Copied into a clamped array of its own rather than wrapping the decoder's
  // buffer: `ImageData` will not take a view on a SharedArrayBuffer, which is
  // what the decoder's return type admits to being.
  const pixels = new Uint8ClampedArray(rgba.length);
  pixels.set(rgba);

  context.putImageData(new ImageData(pixels, width, height), 0, 0);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("The conversion produced nothing."))),
      "image/png",
    );
  });
}
